"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Tooltip = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _web = require("@react-hookz/web");
var _react = _interopRequireWildcard(require("react"));
var _reactTransitionGroup = require("react-transition-group");
var _useTheme = require("../../theme/utils/useTheme");
var _flyout = require("../flyout");
var _FlyoutArrow = require("../flyout/styled/FlyoutArrow");
var _TooltipFlyoutArrow = require("./components/TooltipFlyoutArrow");
var _TooltipFlyoutContainer = require("./components/TooltipFlyoutContainer");
var _tooltipConstants = require("./tooltipConstants");
var _tooltipTheme = require("./tooltipTheme");
/**
 * For each placement, we need to figure out if we should square off a corner of the flyout
 * to mesh well with the arrow when it's at the edge.
 * @param placement
 */
function placementToSquaredCorner(placement) {
  switch (placement) {
    case "auto":
    case "auto-end":
    case "auto-start":
      return undefined;
    case "top-start":
    case "left-start":
      return "borderBottomRight";
    case "top-end":
    case "right-start":
      return "borderBottomLeft";
    case "right-end":
    case "bottom-end":
      return "borderTopLeft";
    case "bottom-start":
    case "left-end":
      return "borderTopRight";
    default:
      return undefined;
  }
}
function getArrowEdge(placement, offset) {
  if (offset === 0) {
    return false;
  }
  switch (placement) {
    case "auto":
    case "auto-end":
    case "auto-start":
      return false;
    case "top-start":
    case "top":
    case "top-end":
    case "bottom-start":
    case "bottom":
    case "bottom-end":
      return offset > 0 ? "left" : "right";
    case "right-start":
    case "right":
    case "right-end":
    case "left-start":
    case "left":
    case "left-end":
      return offset > 0 ? "top" : "bottom";
    default:
      return false;
  }
}

/**
 * Basic tooltip container.
 * Adds a standard backdrop to the Flyout and should be combined with an
 * inner wrapper like TooltipBody to provide the correct spacing.
 */
const Tooltip = props => {
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const [arrowAtEdge, setArrowAtEdge] = (0, _react.useState)(false);

  // If auto, the actual placement can be different with different target dimensions and/or scroll positions.
  const [actualPlacement, setActualPlacement] = (0, _react.useState)(props.placement || "auto");

  // Initially do not transition until this is true.
  const [showTransition, setShowTransition] = (0, _react.useState)(false);
  const showTransitionRafRef = (0, _react.useRef)();

  // Clean this up so we aren't setting state on unmounted components.
  (0, _react.useEffect)(() => () => {
    if (showTransitionRafRef.current) {
      cancelAnimationFrame(showTransitionRafRef.current);
    }
  }, []);

  // Adds a write-phase modifer to check if the arrow is going to be rendered at the edge of the
  // Flyout's content. If so, we want to render a different arrow shape.
  const arrowEdgeModifier = (0, _react.useMemo)(() => ({
    name: "arrowAtEdge",
    enabled: true,
    phase: "write",
    fn(_ref) {
      let {
        state
      } = _ref;
      if (state.elements.arrow && state.modifiersData.arrow) {
        setActualPlacement(state.placement);
        if (state.modifiersData.arrow.centerOffset !== undefined && Math.abs(state.modifiersData.arrow.centerOffset) > 0) {
          setArrowAtEdge(getArrowEdge(state.placement, state.modifiersData.arrow.centerOffset));
        } else {
          setArrowAtEdge(false);
        }
      }
    }
  }), []);
  const enterDirection = actualPlacement.split("-")[0];
  const {
    isOpen
  } = props;
  const previousIsOpen = (0, _web.usePrevious)(isOpen);
  const showTooltipArrow = props.hasArrow || props.hasArrow === undefined && _tooltipConstants.tooltipFlyoutDefaults.hasArrow;
  return /*#__PURE__*/_react.default.createElement(_reactTransitionGroup.Transition
  // The tooltip should animate on mount so this is always true.
  , {
    in: isOpen,
    timeout: {
      enter: 0,
      exit: _tooltipConstants.ANIMATION_DURATION
    },
    appear: true
  }, transitionStatus => {
    return /*#__PURE__*/_react.default.createElement(_flyout.Flyout, (0, _extends2.default)({}, _tooltipConstants.tooltipFlyoutDefaults, {
      role: "tooltip"
    }, props, {
      // We want to remove this from the stack order so it doesn't interfere with
      // other floating elements participating in a StackOrderContext. When a
      // Tooltip opens on top of another Floating element, like a Flyout, it would
      // be considered the top-most element. Since you cannot close the Tooltip
      // with Escape (currently), Escape would be swallowed up by the Tooltip and
      // nothing would happen. If we want to allow Escape to close the Tooltip, we
      // can remove this line, but we MUST then teach the Tooltip how to close
      // itself until it's requested to open again.
      ignoreStackOrder: true,
      popperProps: {
        ...props.popperProps,
        modifiers: [arrowEdgeModifier, ...(props.popperProps?.modifiers || [])],
        onFirstUpdate: args => {
          if (typeof props.popperProps?.onFirstUpdate === "function") {
            props.popperProps.onFirstUpdate(args);
          }
          showTransitionRafRef.current = requestAnimationFrame(() => {
            setShowTransition(true);
          });
        }
      }
      // Once isOpen is set to false, we want to keep it rendered until the exit animation completes.
      ,
      isOpen: isOpen || !!previousIsOpen && !isOpen && transitionStatus !== "exited"
    }), _ref2 => {
      let {
        getArrowProps
      } = _ref2;
      return /*#__PURE__*/_react.default.createElement(_TooltipFlyoutContainer.TooltipFlyoutContainer, {
        enterDirection: enterDirection,
        transitionStatus: transitionStatus,
        showTransition: showTransition,
        squareCorner: arrowAtEdge && placementToSquaredCorner(actualPlacement) || undefined
      }, props.children, showTooltipArrow && /*#__PURE__*/_react.default.createElement(_TooltipFlyoutArrow.TooltipFlyoutArrow, (0, _extends2.default)({}, getArrowProps(), {
        backgroundColor: props.arrowColor || `var(--sds--tooltip--background-color, ${_tooltipTheme.tooltipTheme.background(themeMode)})`,
        arrowAtEdge: arrowAtEdge,
        size: _FlyoutArrow.defaultArrowSize,
        placement: actualPlacement
      })));
    });
  });
};
exports.Tooltip = Tooltip;
//# sourceMappingURL=Tooltip.js.map